import React from 'react'
import { closest } from './../../Utils/Array'
import {
  numberFormatterWithOptional2Dp,
  round2Dp,
} from './../../Utils/Formatters'
import { removeOutliers } from './../../shared/stats'

// This generates and array of desired target intervals to display
// as graph axis
export var targetIntervals = []
;[...Array(13).keys()].forEach(
  pow =>
    (targetIntervals = [
      ...targetIntervals,
      ...[10, 5, 2.5, 2, 1.5].map(v => v * Math.pow(10, 10 - pow)),
    ]),
)

/**
 * Returns an appropiate axis interval that divides a range by approximately
 * 10 labels. Assumes the range starts at 0
 * @param {number} upperRange
 * @param {number} targetTotalIntervals Tries to return an interval that when
 * multiplied by this number equals the upper range. i.e if you want 5 indices
 * on the axis this number would be passed in as 5
 * @returns {number}
 */
export const getAxisInterval = function(upperRange, targetTotalIntervals = 10) {
  var interval = upperRange / targetTotalIntervals

  interval = closest(targetIntervals, interval)
  // If this interval results in to many indices then use a larger interval
  if (upperRange / interval >= 15) {
    var index = targetIntervals.indexOf(interval)
    index--
    if (index >= 0) interval = targetIntervals[index]
  }

  return interval
}

/**
 * @typedef getGraphInfoResult
 * @property {Array<StatsLegend>} y1Indices The y1 label indices
 * @property {Array<StatsLegend>} y2Indices The y2 label indices, will have the same
 * @property {number} y1Upper
 * length as y1Indices
 *
 * Extracts the info needed to render the graph from the data. I.e. the indices
 * that can be used to read the data.
 * @param {Array<StatsLegend>} legend Assumes array has a length of 2
 * @param {Array<object>} data
 * @param {number} opt_targetTotalIntervals Tries to return an interval that when
 * multiplied by this number equals the upper range. i.e if you want 5 indices
 * on the axis this number would be passed in as 5
 * @returns {getGraphInfoResult}
 */
export const getGraphIndices = function(
  legend,
  data,
  opt_targetTotalIntervals,
  opt_includeOutliers = true,
) {
  // Sort through the data and find the upper and lower bounds for the keys
  var results = getDataBounds(
    legend,
    data,
    opt_targetTotalIntervals,
    opt_includeOutliers,
    true,
  )
  var counters = results.map(t => 0),
    indices = results.map(() => [])

  // Loop over until all the counter are more than their equivilent ranges
  while (counters.some((t, i) => t <= results[i].range)) {
    results.forEach(({ interval, lower }, i) => {
      var counter = counters[i]
      var value = (lower * 10000 + counter * 10000) / 10000
      if (legend[i].showAsPercent) {
        value = {
          key: Math.round(value * 10000) / 10000, // Round to 4DP to avoid
          // rounding errors
          label: numberFormatterWithOptional2Dp(value * 100, true) + '%',
        }
      } else {
        value = {
          key: Math.round(value * 10000) / 10000, // Round to 4DP to avoid
          // rounding errors,
          label: round2Dp(value) + '',
        }
      }
      indices[i].push(value)
      counters[i] = (counters[i] * 10000 + interval * 10000) / 10000
    })
  }

  var result = {}
  indices.forEach((indices, i) => {
    result[`y${i + 1}Indices`] = indices
    // Don't return this info because it is miss leading, i.e. the upper value
    // may not match the upper indices because of matching two ranges to the
    // same number of indices. The upper indices can be grapped by grabbing
    // the last value in the array
    //result[`y${i + 1}Upper`] = results[i].upper
    //result[`y${i + 1}Lower`] = results[i].lower
    //result[`y${i + 1}Range`] = results[i].range
  })

  return result
}

/**
 *
 * @typedef GraphInfoResult
 * @property {number} interval The interval to display for the indices for the
 * y1 data
 * @property {number} range The difference between the lowest and max value in
 * the data
 * @property {number} lower The lowest value
 * @property {number} upper The highest value
 *
 * Get the bounds for the graph from the legend and data.
 * @param {Array<StatsLegend>} legend Assumes array has a length of 2
 * @param {Array<object>} data
 * @param {number} opt_targetTotalIntervals Tries to return an interval that when
 * multiplied by this number equals the upper range. i.e if you want 5 indices
 * on the axis this number would be passed in as 5
 * @param {boolean} opt_includeOutliers Whether or not it should return a range
 * that includes outliers.
 * @param {boolean} opt_zeroBased Whether or not the range should be zero
 * based.
 * @returns {Array<GraphInfoResult>}
 */
export const getDataBounds = function(
  legend,
  data,
  opt_targetTotalIntervals,
  opt_includeOutliers = true,
  opt_zeroBased = false,
) {
  return legend.map(({ key, showAsPercent }) => {
    var mappingFunc = d => {
      if (d[key] === undefined) throw new Error('Value cannot be undefined.')
      return d[key]
    }

    // If there's only one data point then throw in another that's zero based
    if (data.length == 1 || (data.length && opt_zeroBased)) {
      data = data.slice(0)
      var zeroBasedDto = { label: data[0].label }
      legend.forEach(({ key }) => (zeroBasedDto[key] = 0))
      data.push(zeroBasedDto)
    }

    var dataPoints = data.map(mappingFunc)
    if (!opt_includeOutliers) {
      // Don't include outleighers, so calculate the correct range
      // First organise the values ascending
      //var dataPoints = data.map(mappingFunc)
      dataPoints = removeOutliers(dataPoints)
    }

    // Don't skew the range with outliers
    var upper = Math.max(...dataPoints)
    var lower = Math.min(...dataPoints)

    // We want to avoid rounding errors here and we only care about 4
    // decimal places (2 is shown but if its percent we need 4 dp as its rounded
    // up to display)
    var range = upper * 10000 - lower * 10000
    var interval = getAxisInterval(range, opt_targetTotalIntervals) / 10000
    // If we don't start on an interval value, then lower to start on an
    // interval value
    var lowerModulus = (lower * 10000) % (interval * 10000)
    if (lowerModulus != 0) {
      //lower = ((lower * 10000) - (lowerModulus * 10000)) / 10000
      lower = (lower * 10000 - lowerModulus) / 10000
    }

    // If we don't end on an interval then need to move up to the nearest
    // interval
    var upperModulus = (upper * 10000) % (interval * 10000)
    if (upperModulus != 0) {
      upper = (upper * 10000 + (interval * 10000 - upperModulus)) / 10000
      //upper += (interval - upperModulus)
    }

    return {
      interval,
      range: (upper * 10000 - lower * 10000) / 10000,
      lower,
      upper,
    }
  })
}

/**
 * @typedef GridProps
 * @property {number} width The grid width
 * @property {number} height The grid height
 * @property {number} xInterval The distance between each X line
 * @property {number} yInterval The distance between each X line
 * @property {number} xOffset How much to offset the X lines by
 * @property {number} yOffset How much to offset the Y lines by
 *
 * Build an svg grid
 * @param {GridProps} props
 * @returns {Array<SVGElement>}
 */
export const getGridLines = props => {
  var { xOffset, yOffset, xInterval, yInterval, height, width } = props

  if (!xOffset) xOffset = 0

  if (!yOffset) yOffset = 0

  // Modify the offset to be less than the interval and negative
  xOffset = ((xOffset + xInterval) % xInterval) - xInterval
  yOffset = ((yOffset + yInterval) % yInterval) - yInterval

  var lines = []
  for (let i = 0, j = 0; i < width; i += xInterval, j++) {
    lines.push(
      <line
        x1={xInterval * (j + 1) + xOffset}
        y1={0}
        x2={xInterval * (j + 1) + xOffset}
        y2={height}
        stroke="#f2f2f2"
        strokeWidth={1}
      />,
    )
  }

  for (let i = 0, j = 0; i < height; i += yInterval, j++) {
    lines.push(
      <line
        x1={0}
        y1={yInterval * (j + 1) + yOffset}
        x2={width}
        y2={yInterval * (j + 1) + yOffset}
        stroke="#f2f2f2"
        strokeWidth={1}
      />,
    )
  }

  return lines
}

/**
 * Averages arrays into a single array
 * e.g. Converts
 * ```
 * [
 *   [1, 3, 8],
 *   [1, 2, 7],
 *   [2, 2, 10]
 * ]
 * ```
 * into
 * ```
 * [1.3333, 2.3333, 8.3333]
 * ```
 * @param {Array<Array<number>} arrays Each array must be the same size
 * @returns {Array<number>}
 */
export const averageArrayValues = function(arrays) {
  if (arrays.length == 0) return []
  var cumTotals = [],
    totals = []
  arrays.forEach((a, i) => {
    a.forEach((b, index) => {
      if (cumTotals[index] === undefined) cumTotals[index] = 0
      cumTotals[index] += b
      if (totals[index] === undefined) totals[index] = 0
      totals[index] += 1
    })
  })
  return cumTotals.map((val, i) => val / totals[i])
}

/**
 * Count how many arrays have values in them.
 * e.g. Convert
 * ```
 * [
 *   [1],
 *   [1, 2],
 *   [2, 2, 10]
 * ]
 * ```
 * into
 * ```
 * [3, 2, 1]
 * ```
 */
export const countArrayValues = function(arrays) {
  if (arrays.length == 0) return []
  var cumTotals = []
  arrays.forEach(a => {
    a.forEach((b, index) => {
      if (cumTotals[index] === undefined) cumTotals[index] = 0
      cumTotals[index] += 1
    })
  })
  return cumTotals
}



// WEBPACK FOOTER //
// ./src/Components/Stats/util.js